import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BarChart3, CalendarOff } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { useAttendanceReportQuery } from './useAttendance';
import {
  attendanceAvailability,
  availabilityCopy,
  type BlockWithAttendance,
} from './attendanceAvailability';

interface Props {
  classOfferingId: string;
  /** The class's schedule blocks; attendance is a property of these, not of the class. */
  scheduleBlocks?: ReadonlyArray<BlockWithAttendance> | null;
  /** Admins can turn attendance on themselves; teachers have to ask. */
  canManage?: boolean;
  /** Where an admin goes to change it, when we know. */
  schedulePath?: string;
}

function localISO(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function initialRange() {
  const today = new Date();
  return {
    fromDate: localISO(new Date(today.getFullYear(), today.getMonth(), 1)),
    toDate: localISO(today),
  };
}

function rateLabel(rate: number | null | undefined) {
  if (rate == null) return '—';
  return `${Number(rate).toFixed(2).replace(/\.00$/, '')}%`;
}

export default function AttendanceReport({
  classOfferingId,
  scheduleBlocks,
  canManage = false,
  schedulePath,
}: Props) {
  const availability = attendanceAvailability(scheduleBlocks);
  const { schoolSlug, yearSlug } = useParams();
  const [range] = useState(initialRange);
  const [fromDate, setFromDate] = useState(range.fromDate);
  const [toDate, setToDate] = useState(range.toDate);
  const invalidRange = fromDate > toDate;
  const { data, loading, error } = useAttendanceReportQuery(classOfferingId, fromDate, toDate);
  const report = invalidRange ? undefined : data?.classOffering?.attendanceReport;
  const rollBlocks = (scheduleBlocks ?? []).filter((block) => block.attendanceEnabled && block.id);
  const rollHref = (blockId: string, rollDate: string) =>
    schoolSlug && yearSlug
      ? `/app/${schoolSlug}/years/${yearSlug}/attendance/${blockId}?date=${rollDate}`
      : null;

  if (availability !== 'enabled') {
    const copy = availabilityCopy(availability, canManage);
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarOff className="h-5 w-5" /> {copy.title}
          </CardTitle>
          <CardDescription>{copy.detail}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border border-dashed p-6">
            <p className="text-sm text-muted-foreground">{copy.action}</p>
            {canManage && schedulePath && (
              <Button asChild variant="outline" size="sm" className="mt-3">
                <Link to={schedulePath}>Open this class's schedule</Link>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" /> Attendance Report
            </CardTitle>
            <CardDescription>
              Review recorded attendance across dates. Blank days are not treated as absences.
            </CardDescription>
            {rollBlocks.some((block) => block.id && rollHref(block.id, toDate)) && (
              <div className="mt-3 flex flex-wrap gap-2">
                {rollBlocks.map((block) => {
                  const href = block.id ? rollHref(block.id, toDate) : null;
                  if (!href) return null;
                  return (
                    <Button asChild key={block.id} variant="outline" size="sm">
                      <Link to={href}>Open {block.displayTitle ?? 'roll'} on {toDate}</Link>
                    </Button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="flex items-end gap-2">
            <div>
              <Label htmlFor="attendance-report-from" className="text-xs">From</Label>
              <Input
                id="attendance-report-from"
                type="date"
                value={fromDate}
                max={toDate}
                onChange={(event) => setFromDate(event.target.value)}
                className="w-auto"
              />
            </div>
            <div>
              <Label htmlFor="attendance-report-to" className="text-xs">To</Label>
              <Input
                id="attendance-report-to"
                type="date"
                value={toDate}
                min={fromDate}
                onChange={(event) => setToDate(event.target.value)}
                className="w-auto"
              />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {invalidRange && <p role="alert" className="text-sm text-destructive">From date must be on or before to date.</p>}
        {error && <p role="alert" className="text-sm text-destructive">{error.message}</p>}
        {loading && !report && <p className="text-sm text-muted-foreground">Loading attendance totals…</p>}

        {report && (
          <>
            <p className="text-sm text-muted-foreground">
              {report.recordedDayCount} recorded day{report.recordedDayCount === 1 ? '' : 's'} from {String(report.fromDate)} through {String(report.toDate)}.
            </p>

            {report.students.length === 0 ? (
              <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
                No students are on this class roster.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="p-3 text-left font-medium">Student</th>
                      <th className="p-3 text-right font-medium">Present</th>
                      <th className="p-3 text-right font-medium">Tardy</th>
                      <th className="p-3 text-right font-medium">Absent</th>
                      <th className="p-3 text-right font-medium">Excused</th>
                      <th className="p-3 text-right font-medium">Recorded</th>
                      <th className="p-3 text-right font-medium">Attendance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.students.map((student) => (
                      <tr key={student.person.id} className="border-b last:border-0">
                        <th className="p-3 text-left font-medium">
                          {student.person.lastName}, {student.person.firstName}
                        </th>
                        <td className="p-3 text-right">{student.presentCount}</td>
                        <td className="p-3 text-right">{student.tardyCount}</td>
                        <td className="p-3 text-right">{student.absentCount}</td>
                        <td className="p-3 text-right">{student.excusedCount}</td>
                        <td className="p-3 text-right">{student.recordedCount}</td>
                        <td className="p-3 text-right font-medium">{rateLabel(student.attendanceRate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {report.days.length > 0 && (
              <details className="rounded-md border">
                <summary className="cursor-pointer p-3 text-sm font-medium">Daily totals</summary>
                <div className="overflow-x-auto border-t">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="p-3 text-left font-medium">Date</th>
                        <th className="p-3 text-right font-medium">Present</th>
                        <th className="p-3 text-right font-medium">Tardy</th>
                        <th className="p-3 text-right font-medium">Absent</th>
                        <th className="p-3 text-right font-medium">Excused</th>
                        <th className="p-3 text-right font-medium">Recorded</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.days.map((day) => (
                        <tr key={String(day.date)} className="border-b last:border-0">
                          <th className="p-3 text-left font-medium">{String(day.date)}</th>
                          <td className="p-3 text-right">{day.presentCount}</td>
                          <td className="p-3 text-right">{day.tardyCount}</td>
                          <td className="p-3 text-right">{day.absentCount}</td>
                          <td className="p-3 text-right">{day.excusedCount}</td>
                          <td className="p-3 text-right">{day.recordedCount}</td>
                          {rollBlocks.length > 0 && (
                            <td className="p-3 text-right">
                              {rollBlocks.map((block) => {
                                const href = block.id ? rollHref(block.id, String(day.date)) : null;
                                if (!href) return null;
                                const label = rollBlocks.length > 1
                                  ? `Edit ${block.displayTitle ?? 'roll'}`
                                  : 'Edit roll';
                                return (
                                  <Link key={block.id} to={href} className="ml-3 text-sm font-medium underline">
                                    {label}
                                  </Link>
                                );
                              })}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
